import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageSquare, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const STATUS = { connected: "Connected", token_invalid: "Reconnect required (token expired)", disconnected: "Disconnected" };
const META_ORIGINS = ["https://www.facebook.com", "https://web.facebook.com", "https://business.facebook.com"];

const loadSdk = (appId, version) => new Promise((resolve) => {
  if (window.FB) return resolve(window.FB);
  window.fbAsyncInit = () => { window.FB.init({ appId, autoLogAppEvents: true, xfbml: false, version }); resolve(window.FB); };
  const s = document.createElement("script");
  s.src = "https://connect.facebook.net/en_US/sdk.js"; s.async = true; s.defer = true; s.crossOrigin = "anonymous";
  document.body.appendChild(s);
});

// Per-business WhatsApp Business account connection via Meta Embedded Signup. No secrets are ever shown.
export const WhatsAppConnectCard = ({ canEdit }) => {
  const qc = useQueryClient();
  const { data: v } = useQuery({ queryKey: ["wa-conn"], queryFn: async () => (await api.get("/whatsapp/connection")).data });
  const [pending, setPending] = useState(null);
  const [pin, setPin] = useState("");
  const assets = useRef(null);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["wa-conn"] }); qc.invalidateQueries({ queryKey: ["comm-settings"] }); };
  const act = (fn, ok) => ({ mutationFn: fn, onSuccess: () => { refresh(); toast.success(ok); }, onError: (e) => toast.error(e?.response?.data?.detail?.msg || apiErr(e)) });
  const connect = useMutation(act((b) => api.post("/whatsapp/connect", b), "WhatsApp account connected — live sending stays off"));
  const check = useMutation(act(() => api.post("/whatsapp/template/check"), "Template checked"));
  const disc = useMutation(act(() => api.post("/whatsapp/disconnect"), "WhatsApp disconnected"));
  const live = useMutation(act((enabled) => api.post("/whatsapp/live", { enabled }), "Live setting saved"));

  useEffect(() => {
    const onMsg = (e) => {
      if (!META_ORIGINS.includes(e.origin)) return;
      let m; try { m = typeof e.data === "string" ? JSON.parse(e.data) : e.data; } catch { return; }
      if (m?.type !== "WA_EMBEDDED_SIGNUP") return;
      if (String(m.event || "").startsWith("FINISH") && m.data?.waba_id && m.data?.phone_number_id) assets.current = m.data;
      else if (m.event === "CANCEL") toast.message("WhatsApp signup cancelled");
      else if (m.event === "ERROR") toast.error("Meta signup reported an error");
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const launch = async () => {
    const FB = await loadSdk(v.app_id, v.graph_version);
    FB.login((resp) => {
      const code = resp?.authResponse?.code;
      if (!code || !assets.current) { toast.error("Signup did not finish; nothing was connected"); return; }
      setPending({ code, ...assets.current });  // the code is short-lived: ask for the PIN immediately
    }, { config_id: v.config_id, response_type: "code", override_default_response_type: true, extras: { setup: {}, sessionInfoVersion: "3" } });
  };

  if (!v) return null;
  const c = v.connection;
  const connected = c?.status === "connected";
  return (
    <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4" data-testid="wa-connect-card">
      <div className="flex items-center gap-2"><MessageSquare className="h-4 w-4 text-emerald-600" /><p className="text-sm font-semibold text-slate-900">Your WhatsApp Business number</p>
        <span className="ml-auto text-xs text-slate-500" data-testid="wa-connect-status">{c ? STATUS[c.status] || c.status : "Not connected"}</span></div>
      <p className="mt-1 text-xs text-slate-500">Messages to your customers are sent from your own WhatsApp Business account, never from a shared Pawkt number.</p>
      {!v.platform_ready && (
        <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800" data-testid="wa-platform-blocker">
          <AlertTriangle className="mr-1 inline h-3.5 w-3.5" />WhatsApp onboarding isn't enabled yet. The platform operator must add: {v.platform_missing.join(", ")}.
        </p>
      )}
      {c && (
        <div className="mt-2 grid gap-1 text-xs text-slate-600 sm:grid-cols-2" data-testid="wa-connect-details">
          <span>Number: {c.display_phone || "—"} {c.verified_name && `(${c.verified_name})`}</span>
          <span>Template {c.template?.name} ({c.template?.language}): <b data-testid="wa-template-status">{c.template?.ok ? "Ready" : c.template?.status}</b> {c.template?.detail && `— ${c.template.detail}`}</span>
          <span>Live sending: <b data-testid="wa-live-state">{c.live_enabled && v.global_live ? "On" : "Off"}</b>{!v.global_live && " (platform switch off)"}</span>
          {c.last_error && <span className="text-rose-600">{c.last_error}</span>}
        </div>
      )}
      {pending && (
        <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="wa-pin-step">
          <Input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit WhatsApp PIN" className="w-48" data-testid="wa-pin-input" />
          <Button size="sm" disabled={pin.length !== 6 || connect.isPending} data-testid="wa-pin-submit"
            onClick={() => connect.mutate({ ...pending, pin, template_language: "en" }, { onSettled: () => { setPending(null); setPin(""); } })}>Finish connecting</Button>
        </div>
      )}
      {canEdit && (
        <div className="mt-3 flex flex-wrap gap-2">
          {!connected && <Button size="sm" onClick={launch} disabled={!v.platform_ready || !!pending} data-testid="wa-connect-btn" className="bg-emerald-600 text-white hover:bg-emerald-700">{c ? "Reconnect WhatsApp" : "Connect WhatsApp"}</Button>}
          {connected && <Button size="sm" variant="outline" onClick={() => check.mutate()} disabled={check.isPending} data-testid="wa-template-check-btn">Check template</Button>}
          {connected && c.template?.ok && <Button size="sm" variant="outline" onClick={() => live.mutate(!c.live_enabled)} data-testid="wa-live-toggle">{c.live_enabled ? "Turn live sending off" : "Allow live sending"}</Button>}
          {c && c.status !== "disconnected" && <Button size="sm" variant="outline" className="text-rose-600" data-testid="wa-disconnect-btn"
            onClick={() => window.confirm("Disconnect this WhatsApp number? Pawkt will stop sending WhatsApp messages for your business.") && disc.mutate()}>Disconnect</Button>}
        </div>
      )}
    </div>
  );
};
